export const PLATFORM_DASHBOARD_TILE = {
  borderRadius: '10px',
  minHeight: 174,
  borderColor: '#e2e8f0',
  iconSize: 48,
  valueFontSize: '30px',
  valueLineHeight: '36px',
  labelFontSize: '14px',
  labelLineHeight: '20px',
  sectionTitleFontSize: '18px',
  sectionTitleColor: '#23262f',
} as const

/** Figma 37:1474 — platform dashboard tab strip */
export const PLATFORM_DASHBOARD_TABS = {
  gap: '4px',
  p: '4px',
  borderRadius: '18px',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.06)',
  tabBorderRadius: '14px',
  tabPx: '16px',
  tabPy: '10px',
  tabInnerGap: '8px',
  /** Fixed tab widths from Figma — creates visual separation between items */
  tabWidths: {
    people: 191,
    revenue: 266,
    tokens: 238,
  } as const,
} as const

export const PLATFORM_POPULATION_TILES = [
  {
    key: 'activeSensemakers',
    label: 'Total Active Sensemakers',
    bg: '#daf5e4',
    iconBg: '#3f8367',
    labelColor: '#3f8367',
  },
  {
    key: 'totalSignalProviders',
    label: 'Total Signal Providers',
    bg: '#fae6dc',
    iconBg: '#df6534',
    labelColor: '#df6534',
  },
  {
    key: 'verifiedEkyc',
    label: 'Total Verified Signal Providers',
    bg: '#c7ebf9',
    iconBg: '#0c9eeb',
    labelColor: '#0c9eeb',
    na: true,
  },
  {
    key: 'failedEkyc',
    label: 'Total Failed eKYC',
    bg: '#fae3ed',
    iconBg: '#ff5959',
    labelColor: '#ff5959',
    na: true,
  },
] as const

export const PLATFORM_GROWTH_TILES = [
  {
    key: 'mobileSignups',
    label: 'Total Mobile Sign-ups',
    bg: '#daf5e4',
    iconBg: '#3f8367',
    labelColor: '#3f8367',
    toggleTrackBg: '#f0faf3',
    accentColor: '#3f8367',
    toggle: true,
    format: 'count' as const,
  },
  {
    key: 'newSensemakerSignups',
    label: 'New Sensemaker Sign-ups',
    bg: '#fae6dc',
    iconBg: '#df6534',
    labelColor: '#df6534',
    toggleTrackBg: '#fff7f3',
    accentColor: '#df6534',
    toggle: true,
    format: 'count' as const,
  },
  {
    key: 'sensemakerConversionRate',
    label: 'Sensemaker Sign-up Conversion Rate',
    bg: '#fae3ed',
    iconBg: '#ff5959',
    labelColor: '#ff5959',
    format: 'percent' as const,
  },
  {
    key: 'profileCompletionRate',
    label: 'Signal Provider Profile Completion Rate',
    bg: '#c7ebf9',
    iconBg: '#0c9eeb',
    labelColor: '#0c9eeb',
    format: 'percent' as const,
  },
] as const

/** Chart panel tokens — Figma node 37:1232 (Growth Trend) + 37:1295 (donut row). */
export const PLATFORM_CHART_CARD = {
  growthTrend: {
    height: 480,
    borderRadius: '14px',
    bgcolor: '#fff',
    boxShadow: '0px 1.418px 4.255px rgba(13, 10, 44, 0.08)',
    paddingX: 1.5,
    paddingY: 1.5,
    paddingBottom: 0.75,
    /** Gap below x-axis title ("Week" / "Month") before the legend */
    xTitleToLegendGap: 16,
    plotMinHeight: 320,
    titleColor: '#1e1b39',
    titleFontSize: '16px',
    titleFontWeight: 600,
    titleFontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
    yAxisTitle: 'Sign-ups',
    axisTitleColor: '#000',
    axisTitleFontFamily: 'Manrope, Inter, sans-serif',
    axisTitleFontSize: 11,
    tickColor: '#615e83',
    tickFontSize: 11,
  },
  standard: {
    borderRadius: '10px',
    border: '1px solid #e6e8ec',
    bgcolor: '#fff',
    titleColor: '#1e1b39',
    titleFontSize: '16px',
    titleFontWeight: 600,
    titleFontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
    titleLineHeight: '20px',
  },
  /** Figma 37:1293 / 37:1320 — donut row card shell (title → chart → legend spacing). */
  donutCard: {
    height: 400,
    paddingTop: '15px',
    paddingX: '16px',
    paddingBottom: '16px',
    titleToBodyGap: '1px',
    chartToLegendGap: '120px',
    chartSize: 288,
    legendWidthEkyc: 97,
    legendWidthSignup: 107,
  },
  line: {
    sensemakers: '#008cd0',
    /** Figma 37:1281 — light fill under the primary growth curve */
    sensemakersFill: 'rgba(0, 140, 208, 0.14)',
    signalProviders: '#df6534',
    grid: '#e5e7eb',
    tick: '#615e83',
  },
  /** Figma node 37:1292 / legend 37:1296 — eKYC Status Breakdown donut */
  ekyc: {
    verified: '#16a34a',
    unverified: '#d97706',
    failed: '#dc2626',
    legendLabel: '#6b7280',
    legendSwatchWidth: '26.24px',
    legendSwatchHeight: '11.48px',
    legendSwatchRadius: '3.28px',
    legendRowHeight: '20px',
    legendRowGap: '11px',
    legendItemGap: '11.48px',
    legendLineHeight: '19.68px',
    centerFontSize: '19px',
    centerLabelFontSize: '18px',
    centerLineHeight: '14px',
    centerLabelGap: '8px',
    centerFontWeight: 700,
    centerColor: '#000',
  },
  /** Figma node 37:1320 — Sign-up Source Split donut */
  signupSource: {
    legendLabel: '#6b7280',
    legendSwatchWidth: '22.72px',
    legendSwatchHeight: '9.94px',
    legendSwatchRadius: '2.84px',
    legendRowHeight: '18px',
    legendRowGap: '9px',
    legendItemGap: '10px',
    legendLineHeight: '18px',
    centerLabel: 'Sensemakers',
  },
  /** Figma node 37:1352 — Demographic Profile Completeness bar chart */
  demographic: {
    height: 500,
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    bgcolor: '#fff',
    boxShadow: '0px 4px 8px rgba(15, 23, 42, 0.07)',
    paddingTop: '26px',
    paddingBottom: '22px',
    paddingLeft: '16px',
    paddingRight: '28px',
    titleColor: '#0f172a',
    titleFontSize: '14px',
    titleFontWeight: 700,
    titleFontFamily: 'Inter, sans-serif',
    titleLetterSpacing: '-0.14px',
    titleLineHeight: '21px',
    titleToBodyGap: '16px',
    axisLabel: '#6b7280',
    axisFontSize: 10,
    yAxisLabelFontSize: 10,
    grid: '#e5e7eb',
    barHigh: '#51a3e9',
    barMedium: '#dc8117',
    barLow: '#e03b3b',
    barRadius: 5,
    barThickness: 16,
    hoverPillHeight: 27,
    hoverPillRadius: '10px',
    hoverPillBorder: '#00adee',
  },
  /** Figma node 37:1424 — New Sign-ups by Day bar chart */
  newSignupsByDay: {
    height: 500,
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    bgcolor: '#fff',
    boxShadow: '0px 4px 8px rgba(15, 23, 42, 0.07)',
    paddingTop: '26px',
    paddingBottom: '22px',
    paddingLeft: '16px',
    paddingRight: '28px',
    titleColor: '#0f172a',
    titleFontSize: '14px',
    titleFontWeight: 700,
    titleFontFamily: 'Inter, sans-serif',
    titleLetterSpacing: '-0.14px',
    titleLineHeight: '21px',
    titleToBodyGap: '16px',
    axisLabel: '#6b7280',
    axisFontSize: 10,
    grid: '#e5e7eb',
    barSensemakers: '#008cd0',
    barSignalProviders: '#1976d2',
    barRadius: 5,
    toggleWidth: 280,
  },
} as const

/** Figma segment colors — keyed by `SensemakerSignupSource` value. */
export const SIGNUP_SOURCE_COLORS: Record<string, string> = {
  google_ads: '#4a9fe8',
  organic: '#d97706',
  meta: '#e91e63',
  paid_landing_pages: '#16c2a8',
  linkedin: '#6366f1',
  unknown: '#94a3b8',
}

export function getSignupSourceColor(source: string, index: number): string {
  return SIGNUP_SOURCE_COLORS[source] ?? Object.values(SIGNUP_SOURCE_COLORS)[index % 4] ?? '#94a3b8'
}

/** Figma tiered bar colors — high ≥63%, medium ≥45%, low below. */
export function getDemographicBarColor(percent: number): string {
  const { barHigh, barMedium, barLow } = PLATFORM_CHART_CARD.demographic
  if (percent >= 63) return barHigh
  if (percent >= 45) return barMedium
  return barLow
}

export const EKYC_STATUS_SEGMENTS = [
  { key: 'verified', label: 'Verified', color: PLATFORM_CHART_CARD.ekyc.verified },
  { key: 'unverified', label: 'Unverified', color: PLATFORM_CHART_CARD.ekyc.unverified },
  { key: 'failed', label: 'Failed', color: PLATFORM_CHART_CARD.ekyc.failed },
] as const
